<template>
    <Dialog
        class="select-interests-dialog"
        :isPopup="isDialogSelectInterestsOpened"
        :hasCloseButton="true"
        :scrollable="false"
        :background="true"
        :overlayOffset="false"
        @close="closeModal"
    >
        <div class="select-interests">
            <div class="select-interests__content">
                <IconVera class="select-interests__icon" />
                <h2 class="select-interests__title">Возможности для тебя</h2>
                <p class="select-interests__description">
                    Выбери то, что тебе интересно, и мы подберем возможности специально для тебя!
                </p>
                <div class="select-interests__group">
                    <h3 class="select-interests__group-title">Направления</h3>
                    <div class="select-interests__list">
                        <div
                            class="select-interests__list-item select-interests__list-item--course-area"
                            v-for="courseArea in allCourseAreas"
                            :key="courseArea.id"
                            :class="{
                                'select-interests__list-item--selected': checkSelectedInterest(
                                    courseArea,
                                    selectedCourseAreas
                                ),
                            }"
                            @click="selectCourseArea(courseArea)"
                        >
                            {{ courseArea.name }}
                        </div>
                    </div>
                </div>
                <div class="select-interests__group">
                    <h3 class="select-interests__group-title">Компетенции</h3>
                    <div class="select-interests__list">
                        <div
                            class="select-interests__list-item select-interests__list-item--competency"
                            v-for="competency in allCompetencies"
                            :key="competency.id"
                            :class="{
                                'select-interests__list-item--selected': checkSelectedInterest(
                                    competency,
                                    selectedCompetencies
                                ),
                            }"
                            @click="selectCompetency(competency)"
                        >
                            {{ competency.name }}
                        </div>
                    </div>
                </div>
            </div>
            <footer class="select-interests__footer">
                <button
                    class="select-interests__button"
                    :class="{ 'select-interests__button--loading': isLoading }"
                    :disabled="isLoading"
                    @click="onGetRecommendations"
                >
                    {{ isLoading ? 'Загрузка возможностей...' : 'Подобрать возможности' }}
                </button>
                <button class="select-interests__button-reset" @click="resetInterests">Сбросить</button>
            </footer>
        </div>
    </Dialog>
</template>

<script>
import Dialog from '@/components/UI/Dialog.vue';
import IconVera from '@/views/track/icons/IconVera.vue';
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'SelectInterestsDialog',
    components: {
        Dialog,
        IconVera,
    },
    data() {
        return {
            allCourseAreas: [],
            allCompetencies: [],
            selectedCourseAreas: [],
            selectedCompetencies: [],
            isLoading: false,
        };
    },
    computed: {
        ...mapGetters('track', ['isDialogSelectInterestsOpened', 'userCompetencies', 'userCourseAreas']),
        ...mapGetters('auth', ['isAuthorized', 'isAuthReady']),
    },
    methods: {
        ...mapActions('track', [
            'closeDialogSelectInterests',
            'fetchAllInterests',
            'addUserCompetence',
            'addUserCourseArea',
            'fetchMyInterests',
            'fetchMyRecommendations',
        ]),
        closeModal() {
            this.closeDialogSelectInterests();
        },
        selectCourseArea(courseArea) {
            const index = this.selectedCourseAreas.findIndex((area) => area.id === courseArea.id);
            if (index > -1) {
                this.selectedCourseAreas.splice(index, 1);
            } else {
                this.selectedCourseAreas.push(courseArea);
            }
        },
        selectCompetency(competency) {
            const index = this.selectedCompetencies.findIndex((comp) => comp.id === competency.id);
            if (index > -1) {
                this.selectedCompetencies.splice(index, 1);
            } else {
                this.selectedCompetencies.push(competency);
            }
        },
        async onGetRecommendations() {
            this.isLoading = true;
            try {
                if (this.isAuthorized) {
                    // Если авторизован, то сохраняем на сервере компетенции и направления
                    await Promise.all([
                        this.addUserCompetence(this.selectedCompetencies.map((comp) => comp.id)),
                        this.addUserCourseArea(this.selectedCourseAreas.map((area) => area.id)),
                    ]);

                    // Получаем обновленный список компетенций и направлений у пользователя
                    await this.fetchMyInterests(true);
                } else {
                    // Если не авторизован, то сохраняем в LocalStorage
                    localStorage.setItem('selectedCompetencies', JSON.stringify(this.selectedCompetencies));
                    localStorage.setItem('selectedCourseAreas', JSON.stringify(this.selectedCourseAreas));

                    // Получаем обновленный список компетенций и направлений у пользователя
                    await this.fetchMyInterests(false);
                }

                if (this.$route.path !== '/account/track') {
                    this.$router.push('/account/track');
                } else {
                    await this.fetchMyRecommendations();
                }

                this.closeModal();
            } catch (error) {
                console.error('Ошибка при получении рекомендаций:', error);
            } finally {
                this.isLoading = false;
            }
        },
        checkSelectedInterest(interest, selectedInterests) {
            return selectedInterests.some((item) => item.id === interest.id);
        },
        async getAllInterests() {
            const interests = await this.fetchAllInterests();
            interests.forEach((interest) => {
                switch (interest.type) {
                    case 'course_area':
                        this.allCourseAreas.push(interest);
                        break;
                    case 'competence':
                        this.allCompetencies.push(interest);
                        break;
                }
            });
        },
        setupMyInterests() {
            this.selectedCourseAreas = [...this.userCourseAreas];
            this.selectedCompetencies = [...this.userCompetencies];
        },
        resetInterests() {
            this.selectedCourseAreas = [];
            this.selectedCompetencies = [];
        },
    },
    watch: {
        isDialogSelectInterestsOpened(isOpened) {
            if (isOpened) {
                this.setupMyInterests();
            }
        },
    },
    async mounted() {
        await this.getAllInterests();
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';
@import '~@/styles/variables';

.select-interests-dialog {
    padding: 0;
    margin: 0;

    :deep(.dialog) {
        max-height: 100vh;
        height: 100%;
        @include on-desktop {
            padding: urem(40) 0;
            width: urem(974);
            height: 100%;
            max-height: 80%;
        }
    }
}

.select-interests {
    font-family: $font-montserrat;
    display: grid;
    height: 100vh;

    @include on-desktop {
        height: 100%;
        display: grid;
        grid-template-rows: 1fr;
    }

    * {
        font-family: inherit;
    }

    &__content {
        padding: urem(20);
        padding-top: urem(48);
        height: 100%;
        overflow-y: auto;

        @include on-desktop {
            padding: urem(24) urem(40);
        }
    }

    &__title {
        font-size: urem(18);
        font-weight: 600;
        line-height: urem(28);
        text-transform: uppercase;
        text-align: center;
        margin: urem(24) 0;
        margin-bottom: urem(16);
        color: rgba(42, 38, 38, 1);

        @include on-desktop {
            font-size: urem(24);
            line-height: urem(32);
        }
    }

    &__icon {
        width: urem(131);
        height: urem(116);
        display: block;
        margin: 0 auto;
    }

    &__description {
        font-weight: 400;
        font-size: urem(16);
        line-height: urem(24);
        text-align: center;
        margin: 0;
        margin-bottom: urem(32);

        @include on-desktop {
            font-size: urem(18);
            line-height: urem(26);
        }
    }

    &__group {
        margin-bottom: urem(32);
    }

    &__list {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: urem(10);
        padding-bottom: urem(50);

        @include on-desktop {
            padding-bottom: urem(30);
            gap: urem(16);
        }
    }

    &__group-title {
        font-size: urem(16);
        line-height: urem(24);
        text-align: center;
        margin: 0;
        margin-bottom: urem(16);

        @include on-desktop {
            font-size: urem(20);
            line-height: urem(32);
        }
    }

    &__list-item {
        display: flex;
        align-items: center;
        gap: urem(8);
        padding: urem(8) urem(16);
        border: urem(1) solid rgba(70, 99, 171, 1);
        border-radius: urem(20);
        line-height: urem(20);
        font-size: urem(14);
        font-weight: 500;
        color: rgba(70, 99, 171, 1);
        background: none;
        box-shadow: 0 0 urem(8) 0 rgba(68, 48, 126, 0.24);
        cursor: pointer;

        @include on-desktop {
            padding: urem(9) urem(20);
            font-size: urem(16);
            min-height: urem(40);
            line-height: urem(22);
        }

        &--course-area {
            border: urem(1) solid rgba(70, 99, 171, 1);
            color: rgba(70, 99, 171, 1);
            transition: all 0.3s ease-in-out;
            cursor: pointer;

            &:hover {
                background: rgba(70, 99, 171, 1);
                color: white;
            }
        }

        &--competency {
            border: urem(1) solid rgba(105, 96, 159, 1);
            color: rgba(105, 96, 159, 1);
            transition: all 0.3s ease-in-out;
            cursor: pointer;

            &:hover {
                background: rgba(105, 96, 159, 1);
                color: white;
            }
        }

        &--course-area.select-interests__list-item--selected {
            color: white;
            background: rgba(70, 99, 171, 1);
        }

        &--competency.select-interests__list-item--selected {
            color: white;
            background: rgba(105, 96, 159, 1);
        }
    }

    &__footer {
        height: urem(140);
        padding: urem(24);
        padding-bottom: urem(8);
        padding-right: 0;
        position: fixed;
        bottom: 0;
        left: 0;
        width: calc(100% - #{urem(24)});
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        background: linear-gradient(
            182.9deg,
            rgba(255, 255, 255, 0.3) -0.77%,
            rgba(255, 255, 255, 0.95) 36.45%,
            #ffffff 97.59%
        );

        @include on-desktop {
            height: urem(140);
            position: absolute;
            padding-left: urem(35);
            padding-bottom: urem(16);
            bottom: 0;
            left: 0;
            width: calc(100% - #{urem(30)});
            border-bottom-left-radius: urem(16);
            border-bottom-right-radius: urem(16);
        }
    }

    &__button-reset {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: urem(16);
        line-height: urem(24);
        font-weight: 600;
        background: none;
        color: #db6c61;
        border: none;
        margin-top: urem(16);
        cursor: pointer;
    }

    &__button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: urem(8);
        font-size: urem(14);
        line-height: urem(42);
        font-weight: 600;
        background-color: rgba(70, 99, 171, 1);
        color: white;
        border: none;
        border-radius: urem(10);
        padding: 0 urem(24);

        &--loading {
            animation: blink 1s linear infinite;
            cursor: not-allowed;
        }

        :deep(.icon-circle-arrows) {
            width: urem(20);
            height: urem(20);
            stroke: white;
        }

        @include on-desktop {
            line-height: urem(48);
            width: fit-content;
            margin: 0 auto;
            transition: background 0.3s ease;
            cursor: pointer;

            &:hover {
                background: rgba(82, 112, 184, 1);
            }
        }
    }
}

@keyframes blink {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
    100% {
        opacity: 1;
    }
}
</style>
